/**
 * Calendar Types
 *
 * Type definitions for the calendar system including events, views, and metadata.
 */

/**
 * Available colors for calendar events
 * These match the --event-* CSS variables defined in app.css
 */
export type EventColor =
    | 'blue'
    | 'gray'
    | 'purple'
    | 'red'
    | 'green'
    | 'yellow'
    | 'orange'
    | 'cyan'
    | 'emerald'
    | 'lime'
    | 'teal'
    | 'indigo'
    | 'violet'
    | 'pink'
    | 'rose'
    | 'amber'
    | 'magenta'
    | 'slate';

/**
 * Calendar view modes
 */
export type CalendarView = 'day' | 'week' | 'month';

/**
 * Types of calendar events
 */
export type CalendarEventType = 'job' | 'site_visit' | 'lead';

/**
 * Vehicle info for events
 */
export interface EventVehicle {
    id: string;
    name: string;
    registration: string;
}

/**
 * Employee info for events
 */
export interface EventEmployee {
    id: string;
    name: string;
}

/**
 * Base calendar event interface
 * Matches the format returned by CalendarController.php
 */
export interface CalendarEvent {
    /** Unique identifier for the event */
    id: string;
    /** Event type (job or site_visit) */
    type: CalendarEventType;
    /** Event title/name */
    title: string;
    /** Event start date/time (ISO 8601 string) */
    start: string | null;
    /** Event end date/time (ISO 8601 string) */
    end: string | null;
    /** Whether event is all day */
    allDay: boolean;
    /** Type-specific metadata */
    meta: JobEventMeta | SiteVisitEventMeta | LeadEventMeta;
}

/**
 * Metadata for job-type events
 * Matches the format returned by CalendarController.php
 */
export interface JobEventMeta {
    /** Job status identifier */
    status: string | null;
    /** Human-readable status label */
    statusLabel: string | null;
    /** Status color */
    statusColor: string | null;
    /** Job type identifier */
    jobType: string | null;
    /** Human-readable job type label */
    jobTypeLabel: string | null;
    /** Primary customer name */
    customerName: string | null;
    /** Customer ID */
    customerId: string | null;
    /** Formatted job address */
    address: string | null;
    /** Postcode from job address */
    postcode: string | null;
    /** Assigned vehicles */
    assignedVehicles: EventVehicle[];
    /** Job subtotal */
    subtotal: number;
}

/**
 * Metadata for site visit events
 * Matches the format returned by CalendarController.php
 */
export interface SiteVisitEventMeta {
    /** Type of visitable entity (Job, Lead, etc.) */
    visitableType: string;
    /** Visitable entity ID */
    visitableId: string;
    /** Visitable entity name/title */
    visitableName: string;
    /** Visit purpose */
    purpose: string | null;
    /** Human-readable purpose label */
    purposeLabel: string | null;
    /** Purpose color */
    purposeColor: string | null;
    /** Visit status */
    status: string | null;
    /** Human-readable status label */
    statusLabel: string | null;
    /** Status color */
    statusColor: string | null;
    /** Visit notes */
    notes: string | null;
    /** Address from the visitable */
    address: string | null;
    /** Postcode from the visitable's address */
    postcode: string | null;
    /** Assigned employees */
    assignedEmployees: EventEmployee[];
    /** Assigned vehicle ID */
    vehicleId: string | null;
    /** Assigned vehicle name */
    vehicleName: string | null;
    /** Assigned vehicle registration */
    vehicleRegistration: string | null;
}

/**
 * Metadata for lead-type events
 * Matches the format returned by CalendarController.php
 */
export interface LeadEventMeta {
    /** Lead status identifier */
    status: string | null;
    /** Human-readable status label */
    statusLabel: string | null;
    /** Status color */
    statusColor: string | null;
    /** Primary customer name */
    customerName: string | null;
    /** Customer ID */
    customerId: string | null;
    /** Formatted lead address */
    address: string | null;
    /** Postcode from lead address */
    postcode: string | null;
    /** Estimated value of the lead */
    estimatedValue: number | null;
}

/**
 * Calendar key/legend item
 */
export interface KeyItem {
    /** Unique key for filtering (e.g. status value) */
    key: string;
    /** Color indicator */
    color: EventColor;
    /** Label text */
    label: string;
    /** Optional visual variant */
    variant?: 'solid' | 'outline' | 'ghost';
}

/**
 * Job status to color mapping
 */
export const JOB_STATUS_COLORS: Record<string, EventColor> = {
    pending: 'yellow',
    scheduled: 'blue',
    'in-progress': 'cyan',
    'on-hold': 'orange',
    completed: 'green',
    cancelled: 'red',
    approved: 'teal',
    rejected: 'red',
    draft: 'gray',
    archived: 'gray',
};

/**
 * Get the appropriate color for a job status
 *
 * @param status - Job status string
 * @returns EventColor for the status (defaults to 'gray' if not found)
 */
export function getJobStatusColor(status: string): EventColor {
    return JOB_STATUS_COLORS[status.toLowerCase()] || 'gray';
}

/**
 * Type guard to check if event is a job
 */
export function isJobEvent(event: CalendarEvent): event is CalendarEvent & { meta: JobEventMeta } {
    return event.type === 'job';
}

/**
 * Type guard to check if event is a site visit
 */
export function isSiteVisitEvent(event: CalendarEvent): event is CalendarEvent & { meta: SiteVisitEventMeta } {
    return event.type === 'site_visit';
}

/**
 * Type guard to check if event is a lead
 */
export function isLeadEvent(event: CalendarEvent): event is CalendarEvent & { meta: LeadEventMeta } {
    return event.type === 'lead';
}

/**
 * Calendar filter options
 */
export interface CalendarFilters {
    eventType: 'all' | 'job' | 'site_visit';
    status?: string;
}

/**
 * Date range for fetching events
 */
export interface DateRange {
    start_date: string;
    end_date: string;
}
